import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Context } from "../store/appContext";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/userActivityDashboard.css";

const numberFormatter = new Intl.NumberFormat("es-ES");

const getDateKey = (value) => {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString().slice(0, 10);
};

const getTodayKey = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getUserLabel = (activityLog) => {
  if (activityLog.username) {
    return activityLog.username;
  }

  if (activityLog.user_id) {
    return `Usuario #${activityLog.user_id}`;
  }

  return "Usuario sin identificar";
};

const getUserKey = (activityLog) =>
  activityLog.user_id || activityLog.username || "unknown";

const getDurationSeconds = (activityLog) => {
  const storedDuration = Number(activityLog.session_duration);

  if (Number.isFinite(storedDuration) && storedDuration >= 0) {
    return storedDuration;
  }

  if (!activityLog.login_at || !activityLog.logout_at) {
    return null;
  }

  const loginAt = new Date(activityLog.login_at);
  const logoutAt = new Date(activityLog.logout_at);

  if (Number.isNaN(loginAt.getTime()) || Number.isNaN(logoutAt.getTime())) {
    return null;
  }

  const calculatedDuration = Math.round((logoutAt.getTime() - loginAt.getTime()) / 1000);

  return calculatedDuration >= 0 ? calculatedDuration : null;
};

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "0 min";
  }

  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours} h ${minutes} min`;
  }

  if (hours > 0) {
    return `${hours} h`;
  }

  return `${minutes} min`;
};

const formatChartDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "0 h";
  }

  return `${numberFormatter.format(Number((seconds / 3600).toFixed(1)))} h`;
};

const buildAnalytics = (logs) => {
  const todayKey = getTodayKey();
  const usersToday = new Set();
  let sessionsToday = 0;
  let totalDuration = 0;
  let sessionsWithDuration = 0;

  const byDay = new Map();
  const byUser = new Map();

  logs.forEach((activityLog) => {
    const dayKey = getDateKey(activityLog.login_at);
    const duration = getDurationSeconds(activityLog);
    const safeDuration = duration || 0;
    const userKey = getUserKey(activityLog);
    const userLabel = getUserLabel(activityLog);

    if (dayKey === todayKey) {
      sessionsToday += 1;
      usersToday.add(userKey);
    }

    if (duration !== null) {
      totalDuration += duration;
      sessionsWithDuration += 1;
    }

    if (dayKey) {
      const dayData = byDay.get(dayKey) || {
        date: dayKey,
        sessions: 0,
        totalDuration: 0,
      };

      dayData.sessions += 1;
      dayData.totalDuration += safeDuration;
      byDay.set(dayKey, dayData);
    }

    const userData = byUser.get(userKey) || {
      user: userLabel,
      sessions: 0,
      totalDuration: 0,
    };

    userData.sessions += 1;
    userData.totalDuration += safeDuration;
    byUser.set(userKey, userData);
  });

  const sessionsByDay = Array.from(byDay.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((item) => ({
      ...item,
      totalHours: Number((item.totalDuration / 3600).toFixed(2)),
    }));

  const topUsers = Array.from(byUser.values())
    .sort((a, b) => b.sessions - a.sessions || b.totalDuration - a.totalDuration)
    .slice(0, 8)
    .map((item) => ({
      ...item,
      totalHours: Number((item.totalDuration / 3600).toFixed(2)),
    }));

  return {
    kpis: {
      activeUsersToday: usersToday.size,
      sessionsToday,
      averageDuration:
        sessionsWithDuration > 0 ? Math.round(totalDuration / sessionsWithDuration) : 0,
      totalSessions: logs.length,
    },
    sessionsByDay,
    topUsers,
  };
};

const UserActivityDashboard = () => {
  const { store, actions } = useContext(Context);
  const { getUserActivity } = actions;

  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadActivity = useCallback(
    async (nextFilters) => {
      setLoading(true);
      setErrorMessage("");

      const activeFilters = nextFilters || {
        date_from: "",
        date_to: "",
      };
      const result = await getUserActivity({
        date_from: activeFilters.date_from,
        date_to: activeFilters.date_to,
      });

      if (!result?.success) {
        setErrorMessage(
          result?.error || "No se pudo cargar la analítica de actividad.",
        );
      }

      setLoading(false);
    },
    [getUserActivity],
  );

  useEffect(() => {
    loadActivity({
      date_from: "",
      date_to: "",
    });
  }, [loadActivity]);

  const logs = Array.isArray(store.userActivityLogs)
    ? store.userActivityLogs
    : [];

  const analytics = useMemo(() => buildAnalytics(logs), [logs]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    loadActivity(filters);
  };

  const handleClearFilters = () => {
    const cleanFilters = {
      date_from: "",
      date_to: "",
    };

    setFilters(cleanFilters);
    loadActivity(cleanFilters);
  };

  return (
    <div className="container mt-5 user-activity-dashboard-page">
      <div className="page-header user-activity-dashboard-header">
        <div>
          <h1 className="page-title">Analítica de actividad</h1>
          <p className="page-subtitle">
            Indicadores de sesiones, uso diario y usuarios con mayor actividad.
          </p>
        </div>
      </div>

      <form className="activity-dashboard-filters" onSubmit={handleSubmit}>
        <div className="activity-dashboard-filter-group">
          <label htmlFor="analytics_date_from">Desde</label>
          <input
            id="analytics_date_from"
            name="date_from"
            type="date"
            value={filters.date_from}
            onChange={handleChange}
          />
        </div>

        <div className="activity-dashboard-filter-group">
          <label htmlFor="analytics_date_to">Hasta</label>
          <input
            id="analytics_date_to"
            name="date_to"
            type="date"
            value={filters.date_to}
            onChange={handleChange}
          />
        </div>

        <div className="activity-dashboard-filter-actions">
          <button className="btn btn-primary" type="submit" disabled={loading}>
            Filtrar
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={handleClearFilters}
            disabled={loading}
          >
            Limpiar
          </button>
        </div>
      </form>

      {loading && (
        <div className="activity-dashboard-state activity-dashboard-loading">
          Cargando analítica de actividad...
        </div>
      )}

      {!loading && errorMessage && (
        <div className="activity-dashboard-state activity-dashboard-error">
          {errorMessage}
        </div>
      )}

      {!loading && !errorMessage && logs.length === 0 && (
        <div className="activity-dashboard-state activity-dashboard-empty">
          No hay registros de actividad para los filtros seleccionados.
        </div>
      )}

      {!loading && !errorMessage && logs.length > 0 && (
        <>
          <section className="activity-dashboard-kpis">
            <article className="activity-dashboard-kpi">
              <span className="activity-dashboard-kpi-label">
                Usuarios activos hoy
              </span>
              <strong>{numberFormatter.format(analytics.kpis.activeUsersToday)}</strong>
            </article>

            <article className="activity-dashboard-kpi">
              <span className="activity-dashboard-kpi-label">Sesiones hoy</span>
              <strong>{numberFormatter.format(analytics.kpis.sessionsToday)}</strong>
            </article>

            <article className="activity-dashboard-kpi">
              <span className="activity-dashboard-kpi-label">Duración media</span>
              <strong>{formatDuration(analytics.kpis.averageDuration)}</strong>
            </article>

            <article className="activity-dashboard-kpi">
              <span className="activity-dashboard-kpi-label">Total sesiones</span>
              <strong>{numberFormatter.format(analytics.kpis.totalSessions)}</strong>
            </article>
          </section>

          <section className="activity-dashboard-charts">
            <article className="activity-dashboard-chart activity-dashboard-chart-wide">
              <h2>Sesiones por día</h2>
              <div className="activity-dashboard-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.sessionsByDay}>
                    <CartesianGrid stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="sessions" name="Sesiones" fill="#2563eb" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>

            <article className="activity-dashboard-chart activity-dashboard-chart-wide">
              <h2>Tiempo total por día</h2>
              <div className="activity-dashboard-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.sessionsByDay}>
                    <CartesianGrid stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={(value) => `${value} h`} />
                    <Tooltip
                      formatter={(value) => [
                        formatChartDuration(Number(value) * 3600),
                        "Tiempo total",
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="totalHours"
                      name="Tiempo total"
                      stroke="#16a34a"
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </article>

            <article className="activity-dashboard-chart activity-dashboard-chart-wide">
              <h2>Usuarios más activos</h2>
              <div className="activity-dashboard-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.topUsers} layout="vertical">
                    <CartesianGrid stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis dataKey="user" type="category" width={120} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sessions" name="Sesiones" fill="#2563eb" />
                    <Bar dataKey="totalHours" name="Horas" fill="#16a34a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>
          </section>
        </>
      )}
    </div>
  );
};

export default UserActivityDashboard;
